/**
 * WorkstationReadinessHint — one quiet line of workstation truth at the
 * moment of onboarding launch (Golden Image Bank, 2026-08-18).
 *
 * The trial's VM starts building the moment the trial is stamped, and with
 * the golden image it is usually READY before the user finishes profile →
 * apps → team. This hint closes that loop right next to the "open my
 * workspace" CTA — stealth-toned by design:
 *  - ready (desktop answering) → "your workstation is ready"
 *  - ready but the desktop stack still starting → stays on "being set up"
 *    (2026-08-19: "ready" only proves the adapter; promising a desktop that
 *    502s made the first /desktop visit a lie)
 *  - provisioning/waking → "your workstation is still being set up — chat
 *    works the moment you land"
 *  - anything else (none/unknown/stopped, request failure, signed-out) →
 *    renders NOTHING. The hint may only ever reassure; /desktop's boot card
 *    stays the authoritative surface for problems.
 * Never a spinner, never red, never blocks launchWorkspace(). Polls
 * getWorkspaceBootStatus every 20s (the /desktop poller convention,
 * AgentActivity.tsx) while `active`, and stops for good once ready.
 *
 * `account` (2026-08-19, eager org provisioning): watch a specific co_ org
 * being set up instead of the caller's back-pointer workspace — the server
 * validates ownership/membership.
 */

import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';

type HintState = 'ready' | 'building' | null;

const HINT_STYLE: React.CSSProperties = {
  textAlign: 'center',
  marginTop: '0.6rem',
  fontSize: '0.78rem',
  color: '#9A948A',
  textTransform: 'lowercase',
};

export default function WorkstationReadinessHint(
  { active, account }: { active: boolean; account?: string | null },
): React.ReactElement | null {
  const [state, setState] = useState<HintState>(null);

  useEffect(() => {
    if (!active || state === 'ready') return;
    let cancelled = false;

    const poll = async () => {
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) return;
        const token = await fbUser.getIdToken();
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
        const params = new URLSearchParams({ desktop: '1' });
        if (account) params.set('account', account);
        const resp = await fetch(
          `https://us-central1-${projectId}.cloudfunctions.net/getWorkspaceBootStatus?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok || cancelled) return;
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (data?.state === 'ready') {
          // desktopReady false = the desktop renderer is still starting; keep
          // the honest "being set up" line. true or absent (older server) =
          // ready.
          setState(data.desktopReady === false ? 'building' : 'ready');
        } else if (data?.state === 'provisioning' || data?.state === 'waking') setState('building');
        else setState(null);
      } catch {
        /* transient — next poll retries; the hint never alarms */
      }
    };
    void poll();
    const interval = setInterval(() => void poll(), 20_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [active, state, account]);

  if (!active || state === null) return null;
  return (
    <div style={HINT_STYLE} data-testid="workstation-readiness-hint">
      {state === 'ready'
        ? '✓ your workstation is ready'
        : 'your workstation is still being set up — chat works the moment you land'}
    </div>
  );
}
